import { Avatar, Progress, Stack, Text } from '@mantine/core';
import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddFAQsParams, generationFAQsByFileSSE, account } from '@mygpt/app';

import ChatbotImg from '/chatbot.png';
const { ContentWrap } = account;
import { ImportType } from '@/components/Faqs/types';
import { useStepTransition, useStepValue } from '@/hooks/useStepStore';
import { useMatch } from 'react-router-dom';

interface FaqsImportLoadingValue {
  type: ImportType;
  source: File | string;
  customPrompt?: string;
}

export const FaqsImportLoading = () => {
  const matchDatasetInBotPage = useMatch('/:bot_or_assistant/:botId/datasets/:datasetId/data-source/faq');
  const matchDatasetInDatasetPage = useMatch('/datasets/:datasetId/data-source/faq');
  const datasetId = matchDatasetInBotPage?.params?.datasetId || matchDatasetInDatasetPage?.params?.datasetId;

  const value = useStepValue<FaqsImportLoadingValue | undefined>();
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const { startNextTransition } = useStepTransition();
  useLayoutEffect(() => {
    if (typeof value !== 'object' || !value.source || !value.type) return;
    const { customPrompt, source, type } = value;
    const faqs: Omit<AddFAQsParams, 'datasetId'>[] = [];

    if (!datasetId) {
      console.error('failed to import faq, datasetId is undefined');
      return;
    }

    // 使用统一的 SSE 服务函数
    const sse = generationFAQsByFileSSE({
      datasetId,
      file: source as File,
      customPrompt,
    });

    // 监听消息事件
    sse.addEventListener('message', (e: any) => {
      try {
        if (!e.data || !e.data.trim()) return;

        const data = JSON.parse(e.data);
        const { action, progress, value } = data;

        for (const item of value) {
          faqs.push({
            ...item,
            sources: item.sources || (typeof source === 'object' ? source.name : source) || 'system',
          });
        }
        if (action === 'done') {
          sse.close();
          setProgress(100);
          setTimeout(() => {
            startNextTransition(undefined, {
              faqs,
            });
          }, 100);
        } else {
          setProgress(progress);
        }
      } catch (error) {
        console.error(`Failed to parse chunkValue(${e.data}) error`, error);
      }
    });

    // 监听错误事件
    sse.addEventListener('error', (e: any) => {
      console.error('SSE error:', e);
      sse.close();
    });

    // 监听连接关闭事件
    sse.addEventListener('abort', () => {
      console.log('SSE connection aborted');
      sse.close();
    });

    // 监听连接就绪事件（readyState === 2 表示连接关闭）
    sse.addEventListener('readystatechange', (e: any) => {
      console.log('SSE readyState changed:', e.readyState);
      if (e.readyState === 2) {
        // 连接已关闭
        sse.close();
        setProgress(100);
        startNextTransition(undefined, {
          faqs,
        });
      }
    });

    return () => {
      sse.close();
    };
  }, [datasetId, startNextTransition, value]);

  return (
    <ContentWrap className="min-w-[40%] md:max-w-[60%] m-auto">
      <Stack gap={28} className="rounded-md bg-white p-10 text-center">
        <div className="flex justify-center">
          <Avatar radius="xl" src={ChatbotImg} size={100} />
        </div>
        <Text className="m-auto" color="gray.8" size="xl">
          {t('faqs.import.loading.description')}
        </Text>
        <Stack gap="sm">
          <Progress value={progress} color="violet.5" radius="xl" size="lg" />
          <Text color="gray.6" fw={300} fz="sm">
            {t('faqs.import.loading.progress', { progress: Math.floor(progress * 100) / 100 })}
          </Text>
        </Stack>
      </Stack>
    </ContentWrap>
  );
};
